import React, { useContext, useEffect, useState } from "react";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import parse from "html-react-parser";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  getToken,
  getUrlWithProtocol,
  useQuery,
} from "../../../../utility/utility";
import "cropperjs/dist/cropper.css";
import EditIcon from "@mui/icons-material/Edit";
import xLogo from "../../../../assets/x_logo.png";
import {
  API_ENDPOINT,
  DEFAULT_DP,
  PROFILE_BANNER,
  PROFILE_IMAGE,
} from "../../../../constants/constants";
import About from "./About";
import Products from "./Products";
import UploadContent from "./UploadContent";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PublicIcon from "@mui/icons-material/Public";
import RdMyModal from "../../../../widgets/myModal/RdMyModal";
import ProfileCropEdit from "./ProfileCropEdit";
import AddCompanyVideo from "../AddCompanyVideo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SocialLinksEdit from "./edit/SocialLinksEdit";
import { UserContext } from "../../../context/UserContext";
import MuPb from "../../../../widgets/MuPb";
import TeamMembers from "../TeamMembers";
import useGetEventSettings from "../../../hooks/useGetEventSettings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SettingsIcon from "@mui/icons-material/Settings";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ImageCropperModal from "../../../../widgets/ImageCropperModal";
import { toast } from "react-toastify";

const ProfileDetails = () => {
  const { user, loading, refreshUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: eD } = useGetEventSettings();
  const queryParams = useQuery();
  const actionTab = queryParams.get("a");

  const [videoMode, setViewMode] = useState(false);
  const [currentTab, setCurrentTab] = useState(
    actionTab ? parseInt(actionTab) : 0
  );
  const [uploadType, setUploadType] = useState(PROFILE_IMAGE);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [showEditSocial, setShowEditSocial] = useState(false);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("a", newValue);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const onEditProfileClick = () => {
    setUploadType(PROFILE_IMAGE);
    setShowImageUpload(true);
  };
  const onEditBannerClick = () => {
    setUploadType(PROFILE_BANNER);
    setShowImageUpload(true);
  };
  const onVideoAdded = () => {
    setShowAddVideo(false);
  };

  if (loading) {
    return <MuPb />;
  }

  return (
    <Box sx={{ backgroundColor: "#f8f9fa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="xl">
        <RdMyModal
          showModal={showEditSocial}
          setShowModal={setShowEditSocial}
          modalC={
            <SocialLinksEdit
              title="Update Social Links"
              user={user}
              show={showEditSocial}
              setShow={setShowEditSocial}
            />
          }
        />

        <ImageCropperModal
          open={showImageUpload}
          onClose={() => setShowImageUpload(false)}
          aspectRatio={uploadType === PROFILE_IMAGE ? 1 : 3}
          imageLabel={uploadType === PROFILE_IMAGE ? "profile photo" : "banner"}
          uploadUrl={`${API_ENDPOINT}/user/upload-profile-image?uploadType=${uploadType}`}
          headers={{ Authorization: `Bearer ${getToken()}` }}
          onSuccess={(response) => {
            toast.success(response.message || `${uploadType === PROFILE_IMAGE ? "Profile photo" : "Banner"} updated successfully.`);
            refreshUser();
          }}
          onError={(error) => console.error("Upload error:", error)}
        />
        <RdMyModal
          mWidth={640}
          showModal={showAddVideo}
          setShowModal={setShowAddVideo}
          modalC={
            <AddCompanyVideo
              exhibitor={user}
              onAdded={onVideoAdded}
              showAdd={showAddVideo}
            />
          }
        />

        <Grid container spacing={4}>
          {/* Left Column: Profile Card & Info */}
          <Grid item xs={12} md={4} lg={3}>
            {/* Main Profile Info Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                mb: 3,
                border: "1px solid #eef2f6",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  py: 4,
                }}
              >
                <Box sx={{ position: "relative", mb: 2 }}>
                  <Avatar
                    src={
                      user.profileImage !== undefined
                        ? user.profileImage
                        : DEFAULT_DP
                    }
                    alt="profile"
                    sx={{
                      width: 120,
                      height: 120,
                      border: "4px solid white",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                    }}
                  />
                  {!videoMode && (
                    <Tooltip title="Change profile photo · Recommended 1080 × 1080 px">
                      <IconButton aria-label="Change profile photo" onClick={onEditProfileClick}
                        sx={{ position: "absolute", bottom: 0, right: 0, width: 36, height: 36,
                          color: "white", bgcolor: "#0288d1", boxShadow: 2,
                          "&:hover": { bgcolor: "#01579b" } }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  color="primary.main"
                  gutterBottom
                >
                  {user.companyName.value}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    mt: 3,
                    width: "100%",
                  }}
                >
                  <Button
                    variant={videoMode ? "contained" : "outlined"}
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setViewMode(true)}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Preview Profile
                  </Button>
                  <Button
                    variant={!videoMode ? "contained" : "outlined"}
                    color="primary"
                    startIcon={<SettingsIcon />}
                    onClick={() => setViewMode(false)}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Update Profile
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Information Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                mb: 3,
                border: "1px solid #eef2f6",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
              }}
            >
              <CardContent sx={{ py: 3 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.8, mb: 2 }}
                >
                  Contact Information
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <LocationOnIcon
                    sx={{ color: "text.secondary", mr: 1.5, fontSize: 20 }}
                  />
                  <Typography
                    component={Link}
                    to={`/hall-view/${user.hallNumber.value}?un=${user.username}`}
                    variant="body2"
                    fontWeight={600}
                    color="primary"
                    sx={{
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Hall {user.hallNumber.value} / Stand{" "}
                    {user.standNumber.value}
                  </Typography>
                </Box>

                {user.website.value !== "" && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <PublicIcon
                      sx={{ color: "text.secondary", mr: 1.5, fontSize: 20 }}
                    />
                    <Typography
                      component="a"
                      href={getUrlWithProtocol(user.website.value)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      fontWeight={600}
                      color="primary"
                      sx={{
                        textDecoration: "none",
                        wordBreak: "break-all",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {user.website.value}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    Social Media
                  </Typography>
                  {!videoMode && (
                    <IconButton
                      size="small"
                      onClick={() => setShowEditSocial(true)}
                      sx={{ color: "primary.main" }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <IconButton
                    component="a"
                    href={getUrlWithProtocol(
                      user?.socialLinks?.value?.facebook
                    )}
                    target="_blank"
                    size="small"
                    sx={{
                      backgroundColor: "#eef2f6",
                      color: "#1877F2",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <FacebookIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    component="a"
                    href={getUrlWithProtocol(
                      user?.socialLinks?.value?.instagram
                    )}
                    target="_blank"
                    size="small"
                    sx={{
                      backgroundColor: "#eef2f6",
                      color: "#E1306C",
                      "&:hover": { backgroundColor: "#fce4ec" },
                    }}
                  >
                    <InstagramIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    component="a"
                    href={getUrlWithProtocol(
                      user?.socialLinks?.value?.linkedin
                    )}
                    target="_blank"
                    size="small"
                    sx={{
                      backgroundColor: "#eef2f6",
                      color: "#0A66C2",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                    }}
                  >
                    <LinkedInIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    component="a"
                    href={getUrlWithProtocol(user?.socialLinks?.value?.twitter)}
                    target="_blank"
                    size="small"
                    sx={{
                      backgroundColor: "#eef2f6",
                      "&:hover": { backgroundColor: "#f1f5f9" },
                    }}
                  >
                    <img
                      src={xLogo}
                      alt="x"
                      style={{ height: "14px", width: "14px" }}
                    />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>

            {/* Video Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                mb: 3,
                border: "1px solid #eef2f6",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
              }}
            >
              <CardContent sx={{ py: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                  >
                    Company Video
                  </Typography>
                  {!videoMode && (
                    <IconButton
                      size="small"
                      onClick={() => setShowAddVideo(true)}
                      sx={{ color: "primary.main" }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {user.companyVideo ? (
                  <Box
                    className="responsive-content"
                    sx={{ borderRadius: 2, overflow: "hidden" }}
                  >
                    {parse(user.companyVideo)}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      py: 4,
                      textAlign: "center",
                      backgroundColor: "#f8fafc",
                      borderRadius: 2,
                      border: "1px dashed #cbd5e1",
                    }}
                  >
                    <PlayCircleOutlineIcon
                      sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      No Video Yet
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Banner & Tabs Content */}
          <Grid item xs={12} md={8} lg={9}>
            {/* Banner Section */}
            <Box
              sx={{
                position: "relative",
                height: { xs: 160, sm: 220, md: 280 },
                backgroundImage: `url(${
                  user.profileBanner === undefined || user.profileBanner === ""
                    ? eD?.defProfileBanner
                    : user.profileBanner
                })`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: "16px 16px 0 0",
                boxShadow: "0px -4px 20px rgba(0,0,0,0.05)",
                overflow: "hidden",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "30%",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.3), transparent)",
                },
              }}
            >
              {!videoMode && (
                <Tooltip title="Change banner · Recommended 1080 × 360 px">
                  <IconButton aria-label="Change banner image" onClick={onEditBannerClick}
                    sx={{ position: "absolute", top: 16, right: 16, zIndex: 2, width: 40, height: 40,
                      color: "white", bgcolor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}>
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Tabs Navigation */}
            <Card
              elevation={0}
              sx={{
                borderRadius: "0 0 16px 16px",
                border: "1px solid #eef2f6",
                borderTop: "none",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.02)",
                mb: 3,
              }}
            >
              <Tabs
                value={currentTab}
                variant="scrollable"
                scrollButtons="auto"
                onChange={handleTabChange}
                TabIndicatorProps={{
                  style: { backgroundColor: "#f4741f", height: 3 },
                }}
                sx={{
                  px: 2,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    minWidth: 120,
                    py: 2.5,
                  },
                  "& .Mui-selected": {
                    color: "#f4741f !important",
                  },
                }}
              >
                <Tab label="About" />
                <Tab label="Products" />
                <Tab label="Team Members" />
                <Tab label="Upload Content" />
              </Tabs>
            </Card>

            {/* Tab Content Area */}
            <Box sx={{ mt: 3 }}>
              {currentTab === 0 ? (
                <About
                  isView={videoMode}
                  exhibitor={user}
                  setCurrentTab={setCurrentTab}
                />
              ) : currentTab === 1 ? (
                <Products
                  gc={3}
                  isView={videoMode}
                  exhibitor={user}
                  setCurrentTab={setCurrentTab}
                />
              ) : currentTab === 2 ? (
                <TeamMembers
                  internal={true}
                  gc={3}
                  isView={videoMode}
                  exhibitor={user}
                />
              ) : (
                <UploadContent isView={videoMode} exhibitor={user} />
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProfileDetails;
